/**
 * Queen B Academy - Entrepreneurship Module Data
 * "Start Your Business" content added to each course with BUILD integration
 */

// BUILD Package Pricing
export const BUILD_PACKAGES = {
  foundation: {
    name: 'Foundation',
    price: 997,
    tagline: 'Essential Authority Launch',
    color: '#CD7F32',
    supportDays: 30,
    features: [
      'LLC formation guidance',
      'EIN registration assistance',
      'DOT number filing guidance',
      'MC authority filing guidance',
      'BOC-3 coordination',
      'UCR registration guidance',
      'IRP & IFTA checklist',
      '30 Days compliance support',
      '30 Days dispatch support',
      'Email support'
    ],
    bestFor: 'New entrepreneurs ready to get their authority with guided support'
  },
  accelerator: {
    name: 'Accelerator',
    price: 1997,
    tagline: 'Complete Business Launch',
    color: '#C0C0C0',
    supportDays: 60,
    features: [
      'Everything in Foundation',
      'Drug & Alcohol Consortium setup',
      'Clearinghouse registration',
      'Compliance calendar setup',
      'Carrier packet template',
      '60 Days compliance monitoring',
      '60 Days dispatch support',
      'Priority email support'
    ],
    bestFor: 'Serious entrepreneurs who want comprehensive compliance coverage'
  },
  elite: {
    name: 'Elite',
    price: 2997,
    tagline: 'Premium Business Partner',
    color: '#D4AF37',
    supportDays: 90,
    features: [
      'Everything in Accelerator',
      'Business bank account setup guidance',
      'Factoring company introduction',
      'Fuel card setup assistance',
      'ELD consultation',
      'Business growth blueprint',
      '90 Days compliance support',
      '90 Days dispatch support',
      'Dedicated account manager',
      'Weekly strategy calls'
    ],
    bestFor: 'Entrepreneurs who want a true business partner for their launch'
  }
};

// Entrepreneurship Roadmap Steps (Universal)
export const ENTREPRENEURSHIP_ROADMAP = [
  {
    step: 1,
    title: 'Complete Your Education',
    description: 'Finish this course to build your knowledge foundation',
    icon: 'GraduationCap',
    status: 'current'
  },
  {
    step: 2,
    title: 'Plan Your Business',
    description: 'Use our calculators to understand startup costs and revenue potential',
    icon: 'Calculator',
    status: 'upcoming'
  },
  {
    step: 3,
    title: 'Get Your Authority',
    description: 'Partner with Queen B BUILD to establish your MC/DOT authority',
    icon: 'FileCheck',
    status: 'upcoming'
  },
  {
    step: 4,
    title: 'Stay Compliant',
    description: 'Use Queen B PROTECT to monitor and maintain compliance',
    icon: 'Shield',
    status: 'upcoming'
  },
  {
    step: 5,
    title: 'Grow Your Business',
    description: 'Partner with Queen B GROW for premium dispatch services',
    icon: 'TrendingUp',
    status: 'upcoming'
  }
];

// Course-specific entrepreneurship content
export const ENTREPRENEURSHIP_MODULES = {
  'cdl-mastery-101': {
    title: 'Start Your Trucking Career',
    subtitle: 'From CDL Holder to Business Owner',
    description: 'You\'ve mastered the knowledge. Now it\'s time to build your empire. Whether you want to drive for a company or own your own truck, this module shows you the path.',
    careerPaths: [
      {
        title: 'Company Driver',
        description: 'Work for an established carrier while building experience',
        timeline: 'Immediate',
        earnings: '$50,000 - $70,000/year',
        pros: ['Steady paycheck', 'Benefits available', 'No startup costs', 'Gain experience'],
        cons: ['Less flexibility', 'Income ceiling', 'Not building equity']
      },
      {
        title: 'Lease Operator',
        description: 'Lease a truck from a carrier and run under their authority',
        timeline: '6-12 months experience',
        earnings: '$60,000 - $90,000/year',
        pros: ['More independence', 'Higher earning potential', 'Build toward ownership'],
        cons: ['More responsibility', 'Variable income', 'Still under someone else\'s authority']
      },
      {
        title: 'Owner Operator',
        description: 'Own your truck and run under your own MC authority',
        timeline: '1-2 years experience recommended',
        earnings: '$100,000 - $200,000+/year',
        pros: ['Full independence', 'Build real equity', 'Unlimited income potential', 'Tax advantages'],
        cons: ['Higher startup costs', 'More risk', 'Business management required'],
        recommended: true,
        buildIntegration: true
      }
    ],
    startupCosts: {
      low: 15000,
      mid: 50000,
      high: 150000,
      breakdown: [
        { item: 'Used Truck (Down Payment)', low: 5000, mid: 15000, high: 50000 },
        { item: 'Insurance (First Quarter)', low: 3000, mid: 8000, high: 15000 },
        { item: 'Authority & Compliance', low: 2000, mid: 3000, high: 3000, note: 'Queen B BUILD handles this' },
        { item: 'Permits & Registration', low: 1500, mid: 2000, high: 3000 },
        { item: 'ELD & Equipment', low: 500, mid: 1500, high: 3000 },
        { item: 'Operating Reserve', low: 3000, mid: 10000, high: 25000 },
        { item: 'Misc & Contingency', low: 0, mid: 10500, high: 51000 }
      ]
    },
    callToAction: {
      title: 'Ready to Get Your Authority?',
      description: 'Queen B BUILD handles all the paperwork and compliance so you can focus on driving.',
      buttonText: 'Explore BUILD Packages'
    }
  },
  
  'dispatcher-101': {
    title: 'Start Your Dispatch Business',
    subtitle: 'From Knowledge to Running Your Own Dispatch Operation',
    description: 'You understand how dispatch works. Now build a business around it. Start your own dispatch company or become a top-tier independent dispatcher.',
    careerPaths: [
      {
        title: 'In-House Dispatcher',
        description: 'Work for a trucking company or dispatch service',
        timeline: 'Immediate',
        earnings: '$40,000 - $60,000/year',
        pros: ['Steady income', 'Learn the business', 'Build relationships'],
        cons: ['Limited growth', 'Fixed salary', 'Working for someone else']
      },
      {
        title: 'Independent Dispatcher',
        description: 'Contract with owner-operators to handle their dispatch',
        timeline: '3-6 months experience',
        earnings: '$60,000 - $120,000/year',
        pros: ['Work from anywhere', 'Flexible schedule', 'Scalable income'],
        cons: ['Must find your own clients', 'Variable income', 'No benefits']
      },
      {
        title: 'Dispatch Company Owner',
        description: 'Build a dispatch agency with multiple dispatchers',
        timeline: '1+ year experience',
        earnings: '$150,000 - $500,000+/year',
        pros: ['Unlimited scale', 'Build real equity', 'Multiple revenue streams'],
        cons: ['Management responsibility', 'Higher overhead', 'More complex'],
        recommended: true,
        buildIntegration: true
      }
    ],
    startupCosts: {
      low: 500,
      mid: 5000,
      high: 25000,
      breakdown: [
        { item: 'Computer & Software', low: 300, mid: 1500, high: 5000 },
        { item: 'Load Board Subscriptions', low: 100, mid: 500, high: 1500 },
        { item: 'LLC Formation', low: 100, mid: 500, high: 1000, note: 'Queen B BUILD can help' },
        { item: 'Website & Marketing', low: 0, mid: 1500, high: 10000 },
        { item: 'Operating Reserve', low: 0, mid: 1000, high: 5000 },
        { item: 'Office Setup', low: 0, mid: 0, high: 2500 }
      ]
    },
    callToAction: {
      title: 'Want to Dispatch for Queen B?',
      description: 'We\'re always looking for talented dispatchers. Complete this course and join our dispatch network.',
      buttonText: 'Learn About Partnerships'
    }
  },
  
  'trucking-101': {
    title: 'Start Your Trucking Company',
    subtitle: 'From Understanding the Business to Owning It',
    description: 'You know how trucking companies work. Now build your own. This module walks you through launching and scaling your trucking operation.',
    careerPaths: [
      {
        title: 'Single Truck Operation',
        description: 'Start with one truck - drive it yourself or hire a driver',
        timeline: '0-6 months',
        earnings: '$80,000 - $150,000/year',
        pros: ['Lower startup cost', 'Manageable risk', 'Learn the business'],
        cons: ['Income limited to one truck', 'You\'re the bottleneck']
      },
      {
        title: 'Small Fleet (2-5 Trucks)',
        description: 'Scale to multiple trucks with hired drivers',
        timeline: '1-2 years',
        earnings: '$150,000 - $400,000/year',
        pros: ['Multiplicative income', 'Building real business', 'Can step back from driving'],
        cons: ['Driver management', 'More capital required', 'More complexity']
      },
      {
        title: 'Fleet Owner (6+ Trucks)',
        description: 'Build a full trucking company with systems and processes',
        timeline: '3-5 years',
        earnings: '$500,000 - $2,000,000+/year',
        pros: ['Significant wealth building', 'Sellable asset', 'True business ownership'],
        cons: ['Significant capital', 'Full business management', 'Higher stakes'],
        recommended: true,
        buildIntegration: true
      }
    ],
    startupCosts: {
      low: 20000,
      mid: 75000,
      high: 200000,
      breakdown: [
        { item: 'Truck (Down Payment or Used)', low: 10000, mid: 40000, high: 100000 },
        { item: 'Trailer', low: 5000, mid: 15000, high: 40000 },
        { item: 'Insurance (First Quarter)', low: 3000, mid: 10000, high: 25000 },
        { item: 'Authority & Compliance', low: 1000, mid: 3000, high: 3000, note: 'Queen B BUILD handles this' },
        { item: 'Operating Capital', low: 1000, mid: 7000, high: 32000 }
      ]
    },
    callToAction: {
      title: 'Ready to Launch Your Trucking Company?',
      description: 'Queen B BUILD gets your authority established in 30 days or less. We handle the paperwork, you focus on the business.',
      buttonText: 'Start with BUILD'
    }
  },
  
  'freight-broker-101': {
    title: 'Start Your Freight Brokerage',
    subtitle: 'From Broker Knowledge to Running Your Own Brokerage',
    description: 'You understand freight brokerage. Now build your own. This is one of the most scalable businesses in trucking - and you can start from your laptop.',
    careerPaths: [
      {
        title: 'Broker Agent',
        description: 'Work under an established brokerage\'s authority',
        timeline: 'Immediate',
        earnings: '$50,000 - $100,000/year',
        pros: ['No authority needed', 'Lower risk', 'Learn the business'],
        cons: ['Split commissions', 'Less control', 'Building someone else\'s book']
      },
      {
        title: 'Independent Broker',
        description: 'Get your own broker authority and build your book',
        timeline: '6-12 months experience',
        earnings: '$100,000 - $250,000/year',
        pros: ['Keep all commissions', 'Build equity', 'Full control'],
        cons: ['Bond & insurance required', 'Must build from scratch', 'More responsibility'],
        recommended: true,
        buildIntegration: true
      },
      {
        title: 'Brokerage Company',
        description: 'Build a brokerage with multiple agents',
        timeline: '2-3 years',
        earnings: '$300,000 - $1,000,000+/year',
        pros: ['Highly scalable', 'Recurring revenue', 'Significant exit value'],
        cons: ['Agent management', 'Higher overhead', 'Complex compliance']
      }
    ],
    startupCosts: {
      low: 5000,
      mid: 15000,
      high: 50000,
      breakdown: [
        { item: 'Broker Authority', low: 300, mid: 300, high: 300 },
        { item: 'Surety Bond ($75k)', low: 1500, mid: 2500, high: 5000 },
        { item: 'Insurance', low: 1000, mid: 3000, high: 10000 },
        { item: 'TMS Software', low: 200, mid: 1200, high: 5000 },
        { item: 'Load Boards', low: 500, mid: 1500, high: 3000 },
        { item: 'LLC Formation', low: 500, mid: 1000, high: 2000, note: 'Queen B BUILD can help' },
        { item: 'Operating Capital', low: 1000, mid: 5500, high: 24700 }
      ]
    },
    callToAction: {
      title: 'Ready for Your Broker Authority?',
      description: 'Queen B BUILD helps freight brokers get licensed and compliant. Bond assistance, authority filing, and ongoing support.',
      buttonText: 'Get Started with BUILD'
    }
  }
};

// Interactive Calculator Configs
export const CALCULATORS = {
  startupCost: {
    title: 'Startup Cost Calculator',
    description: 'Estimate your total investment to launch your business',
    icon: 'Calculator'
  },
  profitMargin: {
    title: 'Profit Margin Calculator',
    description: 'Calculate your potential earnings per load',
    icon: 'DollarSign'
  },
  breakEven: {
    title: 'Break-Even Calculator',
    description: 'Find out how many loads you need to cover costs',
    icon: 'Target'
  },
  rateNegotiation: {
    title: 'Rate Calculator',
    description: 'Calculate minimum acceptable rate per mile',
    icon: 'TrendingUp'
  }
};

export default {
  BUILD_PACKAGES,
  ENTREPRENEURSHIP_ROADMAP,
  ENTREPRENEURSHIP_MODULES,
  CALCULATORS
};
